import {z} from "zod";

export const recurringFrequencySchema = z.enum([ 'daily', 'weekly', 'monthly', 'yearly']);
export type RecurringFrequency = z.infer<typeof recurringFrequencySchema>;

export const recurringSchema = z.object({
  frequency: recurringFrequencySchema.optional(),
  interval: z.number().min(1).max(2).optional(), // currently only allow 1 or 2
  // weekdays: z.array(z.number().min(1).max(7)).optional(),
  // monthDays: z.array(z.number().min(1).max(31)).optional(),
  // yearDay: z.string().optional(), // Assuming ISO date format
  until: z.number().optional(),
  // endsAfterOccurrences: z.number().min(1).optional(),
})

export type Recurring = z.infer<typeof recurringSchema>;


export const eventTypeSchema = z.enum(["training", "game" ,"other"]); // "match"

export const eventNotificationSchema = z.enum(['default','none']);
export type EventType = z.infer<typeof eventTypeSchema>;

export const eventResponseModeSchema = z.enum(['disabled', 'default-accepted', 'default-declined', 'default-unknown']);
export type EventResponseMode = z.infer<typeof eventResponseModeSchema>;

export const eventStatusSchema = z.enum(['default', 'canceled', 'deleted']);
export type EventStatus = z.infer<typeof eventStatusSchema>;

export const eventSchema = z.object({
  _id: z.string(),
  appId: z.string().max(10),
  teamIds: z.array(z.string()).optional(),
  memberIds: z.array(z.string()).optional(),
  isClubEvent: z.boolean().optional(),
  start: z.number(),
  end: z.number(),
  title: z.string().min(3),
  description: z.string().optional(),
  location: z.string().optional(),
  image: z.string().optional(),
  recurringEventId: z.string().optional(),
  responseMode: eventResponseModeSchema.default(eventResponseModeSchema.enum.disabled),
  eventType: eventTypeSchema.default(eventTypeSchema.enum.other),
  status: eventStatusSchema.default(eventStatusSchema.enum.default),
  publicVisible: z.boolean().optional(),
  notifications: eventNotificationSchema.default('default'),
  updatedAt: z.number(),
  updatedBy: z.string(),
  createdAt: z.number(),
  createdBy: z.string(),
  ownerId: z.number().optional(),
});
export type Event = z.infer<typeof eventSchema>;

// interface EventOwner {
//   teamIds?: string[];
//   isClubEvent?: boolean;
// }

// function attachEventOwnerRefinement<O extends EventOwner, T extends z.ZodTypeDef, I>(schema: z.ZodType<O, T, I>) {
//   return schema.refine(data => data.isClubEvent || (data.teamIds && data.teamIds.length > 0), {
//     message: 'Wähle ein Team oder den gesamten Verein aus.',
//   });
// }
//
// function ensureTeamIdsOrEntireClub<T extends EventOwner>(data: T, ctx: z.RefinementCtx) {
//   if (!data.isClubEvent && (!data.teamIds || data.teamIds.length === 0)) {
//     ctx.addIssue({
//       code: z.ZodIssueCode.custom,
//       message: 'Wähle ein Team oder den gesamten Verein aus.',
//       path: ['teamIds'],
//     });
//   }
//   return z.NEVER;
// }

export const eventCreateRequestSchema = eventSchema.pick({
  teamIds: true,
  memberIds:true,
  isClubEvent: true,
  start: true,
  end: true,
  title: true,
  description: true,
  location: true,
  image: true,
  eventType: true,
  responseMode: true,
  publicVisible: true,
  notifications: true,
})
  .extend({
  recurring: recurringSchema.superRefine((data, ctx) => {
    if (data.frequency && !data.until) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Wiederholen Ende muss ausgefüllt sein, wenn Wiederholen ausgewählt ist.',
        path: ['until'],
      });
    }
    return z.NEVER;
  }).optional()
}).refine(data => data.isClubEvent || (data.teamIds && data.teamIds.length > 0) || (data.memberIds && data.memberIds.length > 0), {
  message: 'Wähle ein Team, Mitglied oder den gesamten Verein aus.', path: ['memberIds']
});

export type EventCreateRequest = z.infer<typeof eventCreateRequestSchema>;

export const eventCreateFormRequestSchema = eventSchema.pick({
  teamIds: true,
  isClubEvent: true,
  start: true,
  end: true,
  title: true,
  description: true,
  location: true,
  image: true,
  eventType: true,
  responseMode: true,
  publicVisible: true,
  notifications: true,
})
  .extend({
    memberIds:z.array(z.object({Id:z.number()})),
    recurring: recurringSchema.superRefine((data, ctx) => {
      if (data.frequency && !data.until) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Wiederholen Ende muss ausgefüllt sein, wenn Wiederholen ausgewählt ist.',
          path: ['until'],
        });
      }
      return z.NEVER;
    }).optional()
  }).refine(data => data.isClubEvent || (data.teamIds && data.teamIds.length > 0) || (data.memberIds && data.memberIds.length > 0), {
    message: 'Wähle ein Team, Mitglied oder den gesamten Verein aus.', path: ['memberIds']
  });

export type EventCreateFormRequest = z.infer<typeof eventCreateFormRequestSchema>


export const eventUpdateRequestSchema = eventSchema.pick({
  _id: true,
  memberIds: true,
  teamIds: true,
  isClubEvent: true,
  start: true,
  end: true,
  title: true,
  description: true,
  location: true,
  image: true,
  eventType: true,
  responseMode: true,
  publicVisible: true,
}).extend({
  updateFutureRecurring: z.boolean().optional(),
}).refine(data => data.isClubEvent || (data.teamIds && data.teamIds.length > 0) || (data.memberIds && data.memberIds.length > 0), {
  message: 'Wähle ein Team, Mitglied oder den gesamten Verein aus.', path: ['memberIds']
});
export type EventUpdateRequest = z.infer<typeof eventUpdateRequestSchema>;

export const eventUpdateFormRequestSchema = eventSchema.pick({
  _id: true,
  teamIds: true,
  isClubEvent: true,
  start: true,
  end: true,
  title: true,
  description: true,
  location: true,
  image: true,
  eventType: true,
  responseMode: true,
  publicVisible: true,
}).extend({
  memberIds:z.array(z.object({Id:z.number()})),
  updateFutureRecurring: z.boolean().optional(),
}).refine(data => data.isClubEvent || (data.teamIds && data.teamIds.length > 0) || (data.memberIds && data.memberIds.length > 0), {
  message: 'Wähle ein Team, Mitglied oder den gesamten Verein aus.', path: ['memberIds']
});
export type EventUpdateFormRequest = z.infer<typeof eventUpdateFormRequestSchema>;

export const eventUpdateStatusRequestSchema = z.object({
  _id: z.string(),
  status: eventStatusSchema,
  updateFutureRecurring: z.boolean().optional(),
});
export type EventUpdateStatusRequest = z.infer<typeof eventUpdateStatusRequestSchema>;


export const eventDeleteRequestSchema = z.object({
  _id: z.string(),
  updateFutureRecurring: z.boolean().optional(),
});
export type EventDeleteRequest = z.infer<typeof eventDeleteRequestSchema>;

export const eventGetQuerySchema = eventSchema.pick({_id: true});
export type EventGetQuery = z.infer<typeof eventGetQuerySchema>;

export const eventResponseStatusSchema = z.enum(['accepted', 'declined', 'maybe', 'noSelection']);
export type EventResponseStatus = z.infer<typeof eventResponseStatusSchema>;

export const eventResponseSchema = z.object({
  eventId: z.string(),
  personId: z.string(),
  status: eventResponseStatusSchema
});

export type EventResponse = z.infer<typeof eventResponseSchema>;

export const responsesSchema = z.record(z.string(), eventResponseStatusSchema);
export type EventResponses = z.infer<typeof responsesSchema>;

export const eventWithResponsesSchema = eventSchema.extend({
  responses: responsesSchema,
});
export type EventWithResponses = z.infer<typeof eventWithResponsesSchema>;

export const eventListSchema = z.object({
  items: z.array(eventWithResponsesSchema),
  total: z.number()
})

export type EventList = z.infer<typeof eventListSchema>;

function isTrueQueryParam(x?: string) {
  return x && (x.toLowerCase() === 'true' || x === '1');
}

export const eventListQuerySchema = z.object({
  teamIds: z.string().transform((x) => x.split(",").map(x => x.trim())).pipe(z.array(z.string()).optional()).catch(undefined),
  memberIds: z.string().transform((x) => x.split(",").map(x => x.trim())).pipe(z.array(z.string()).optional()).catch(undefined),
  includeClubEvents: z.string().optional().transform(isTrueQueryParam).pipe(z.boolean()).catch(false),
  onlyPublicVisible: z.string().optional().transform(isTrueQueryParam).pipe(z.boolean()).catch(false),
  limit: z.coerce.number().default(50),
  from: z.coerce.number().optional(),
  to: z.coerce.number().optional(),
  ownerId: z.coerce.number().optional(),
});
export type EventListQuery = z.infer<typeof eventListQuerySchema>;

export const recurringEventSchema = recurringSchema.extend({
  _id: z.string(),
  event: eventSchema,
  updatedAt: z.number(),
  updatedBy: z.string(),
  createdAt: z.number(),
  createdBy: z.string(),
});

export const gameEventSchema = z.object({
  _id: z.string(),
  appId: z.string().max(10),
  description: z.string().optional(),
  location: z.string().optional(),
  image: z.string().optional(),
  responseMode: eventResponseModeSchema.default(eventResponseModeSchema.enum.disabled),
  updatedAt: z.number().optional(),
  updatedBy: z.string().optional(),
  createdAt: z.number().optional(),
  createdBy: z.string().optional(),
  userCreated: z.boolean().optional()
});

export type GameEvent = z.infer<typeof gameEventSchema>;

export const gameEventWithResponsesSchema = gameEventSchema.extend({
  responses: responsesSchema,
});
export type GameEventWithResponses = z.infer<typeof gameEventWithResponsesSchema>;

export const gameEventListSchema = z.object({
  items: z.array(gameEventWithResponsesSchema),
  total: z.number()
})

export type GameEventList = z.infer<typeof gameEventListSchema>;

export const gameEventUpsertRequestSchema = gameEventSchema.pick({
  _id: true,
  description: true,
  location: true,
  image: true,
  responseMode: true,
  userCreated:true
});

export type GameEventUpsertRequest = z.infer<typeof gameEventUpsertRequestSchema>;

export const gameEventDeleteRequestSchema = gameEventSchema.pick({
  _id: true,
}).extend({title:z.string(), teamId:z.string()})


export type GameEventDeleteRequest = z.infer<typeof gameEventDeleteRequestSchema>;

export const gameEventGetQuerySchema = gameEventSchema.pick({_id: true});
export type GameEventGetQuery = z.infer<typeof gameEventGetQuerySchema>;

export const gameEventListByIdsQuerySchema = z.object({
  ids: z.string().transform((x) => x.split(",").map(x => x.trim())).pipe(z.array(z.string())).catch([]),
});

export type GameEventListByIdsQuery = z.infer<typeof gameEventListByIdsQuerySchema>;

export interface EventAdminStats {
  totalEvents: number;
  recurringEvents: number;
  nonRecurringEvents: number;
  deletedEvents: number;
  futureEvents: number;
  futureRecurringEvents: number;
}

export interface EventAdminStatsDict {
  [appId: string]: EventAdminStats;
}
